import { useEffect, useState } from "react";
import { fetchCatalog, prepareCheckout } from "../api/catalog";
import { useCart } from "../cart/CartContext";
import StoreHeader from "../components/StoreHeader";

const checkoutBaseUrl = String(
  import.meta.env.VITE_CHECKOUT_URL || "http://localhost:5174"
).replace(/\/$/, "");
const moneyFormatters = new Map();

function formatMoney(amountMinor, currency = "BRL") {
  const key = `${currency}:pt-BR`;
  if (!moneyFormatters.has(key)) {
    moneyFormatters.set(
      key,
      new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency,
      })
    );
  }
  return moneyFormatters.get(key).format(Number(amountMinor) / 100);
}

function itemOptions(item) {
  const values = Object.values(item.options || {}).filter(Boolean);
  return values.length > 0 ? values.join(" / ") : item.variantTitle;
}

function CartSummary({ storeSlug }) {
  const { items, itemCount, subtotalMinor, currency } = useCart();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const startCheckout = async () => {
    setSubmitting(true);
    setError("");
    try {
      const fingerprint = items
        .map((item) => `${item.variantId}:${item.quantity}`)
        .sort()
        .join("|");
      const storageKey = `atomicpay-checkout-key:${storeSlug}:${fingerprint}`;
      let idempotencyKey = sessionStorage.getItem(storageKey);
      if (!idempotencyKey) {
        idempotencyKey = crypto.randomUUID();
        sessionStorage.setItem(storageKey, idempotencyKey);
      }
      const checkout = await prepareCheckout(storeSlug, items, {
        idempotencyKey,
      });
      window.location.assign(
        `${checkoutBaseUrl}/commerce/${encodeURIComponent(
          checkout.checkoutToken
        )}`
      );
    } catch (checkoutError) {
      setError(checkoutError.message);
      setSubmitting(false);
    }
  };

  return (
    <aside className="cart-summary" aria-live="polite">
      <div>
        <span>Subtotal</span>
        <span key={itemCount}>
          {itemCount} {itemCount === 1 ? "item" : "itens"}
        </span>
      </div>
      <div className="cart-summary__total">
        <strong>Total</strong>
        <strong>{formatMoney(subtotalMinor, currency)}</strong>
      </div>
      {error && (
        <p className="cart-summary__error" role="alert">
          {error}
        </p>
      )}
      <button
        type="button"
        className="cart-checkout-button"
        disabled={submitting || items.length === 0}
        onClick={startCheckout}
      >
        {submitting ? "Preparando checkout..." : "Ir para o checkout"}
      </button>
      <p>Frete e condições de pagamento serão definidos no checkout.</p>
    </aside>
  );
}

export default function CartPage({ storeSlug }) {
  const { items, setQuantity, removeItem } = useCart();
  const [store, setStore] = useState(null);
  const storeUrl = `/commerce/${encodeURIComponent(storeSlug)}`;

  useEffect(() => {
    const controller = new AbortController();
    fetchCatalog(storeSlug, {
      page: 1,
      limit: 1,
      signal: controller.signal,
    })
      .then((result) => {
        setStore(result.store);
        document.title = `Carrinho | ${result.store.name}`;
      })
      .catch(() => {});
    return () => controller.abort();
  }, [storeSlug]);

  return (
    <div className="storefront">
      <StoreHeader store={store} storeSlug={storeSlug} />

      <main className="cart-page">
        <nav className="breadcrumb" aria-label="Navegação estrutural">
          <a href={storeUrl}>
            <svg viewBox="0 0 24 24" aria-hidden>
              <path d="m15 18-6-6 6-6" />
            </svg>
            Continuar comprando
          </a>
        </nav>

        <div className="cart-page__heading">
          <div>
            <p className="catalog__eyebrow">Sua seleção</p>
            <h1>Carrinho</h1>
          </div>
        </div>

        {items.length === 0 ? (
          <section className="empty-cart">
            <div className="empty-cart__icon" aria-hidden>
              <svg viewBox="0 0 24 24">
                <path d="M3 4h2l2.2 10.2a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.6L20.2 8H6.1M9.5 20a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Zm8 0a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Z" />
              </svg>
            </div>
            <h2>Seu carrinho está vazio</h2>
            <p>Explore a loja e escolha os produtos que deseja comprar.</p>
            <a href={storeUrl}>Ver produtos</a>
          </section>
        ) : (
          <div className="cart-layout">
            <section className="cart-items" aria-label="Produtos no carrinho">
              {items.map((item) => {
                const productUrl = `${storeUrl}/products/${encodeURIComponent(
                  item.productHandle
                )}`;
                const atMaximum =
                  item.maxQuantity != null &&
                  item.quantity >= item.maxQuantity;

                return (
                  <article className="cart-item" key={item.id}>
                    <a className="cart-item__image" href={productUrl}>
                      {item.image ? (
                        <img src={item.image} alt="" loading="lazy" />
                      ) : (
                        <span aria-hidden>{item.title.slice(0, 1)}</span>
                      )}
                    </a>

                    <div className="cart-item__information">
                      <a href={productUrl}>{item.title}</a>
                      <p>{itemOptions(item)}</p>
                      {item.sku && <small>SKU {item.sku}</small>}
                      <button
                        type="button"
                        className="cart-item__remove"
                        onClick={() => removeItem(item.id)}
                      >
                        Remover
                      </button>
                    </div>

                    <div className="quantity-control">
                      <button
                        type="button"
                        aria-label={`Diminuir quantidade de ${item.title}`}
                        onClick={() =>
                          setQuantity(item.id, item.quantity - 1)
                        }
                      >
                        −
                      </button>
                      <span aria-label={`Quantidade: ${item.quantity}`}>
                        {item.quantity}
                      </span>
                      <button
                        type="button"
                        disabled={atMaximum}
                        aria-label={`Aumentar quantidade de ${item.title}`}
                        onClick={() =>
                          setQuantity(item.id, item.quantity + 1)
                        }
                      >
                        +
                      </button>
                    </div>

                    <div className="cart-item__amount">
                      <strong>
                        {formatMoney(
                          item.amountMinor * item.quantity,
                          item.currency
                        )}
                      </strong>
                      {item.quantity > 1 && (
                        <small>
                          {formatMoney(item.amountMinor, item.currency)} cada
                        </small>
                      )}
                    </div>
                  </article>
                );
              })}
            </section>

            <CartSummary storeSlug={storeSlug} />
          </div>
        )}
      </main>

      <footer className="store-footer">
        <span>Loja criada com</span>
        <strong>AtomicPay</strong>
      </footer>
    </div>
  );
}
